import type { Assets } from '../core/assets.js';
import type { Screen } from '../core/screen.js';
import { SCREEN_H, SCREEN_W } from '../core/screen.js';
import { AudioSystem } from '../audio/audio.js';
import { SEG_FLIGHT } from '../audio/music.js';
import { HumanController, KeyboardController, TouchController } from '../sim/controllers.js';
import { DuelPilot } from '../sim/duel.js';
import { angleTo } from '../sim/math.js';
import { BotController } from '../sim/bot.js';
import { CAM_MAX_X, pilotPosition, type PlaneFrame, type SpriteDraw } from '../sim/sim.js';
import {
  createSim,
  stepWithControllers,
  hashState,
  type Controller,
  type SimState,
} from '../sim/sim.js';
import { FxRandom, ParticlePool, particleSprites } from '../render/particles.js';
import { SmokeTrail } from '../render/smoke.js';
import { profileStore, type MatchResult } from '../ui/profile.js';
import { ResultSequence } from '../render/result.js';
const SKY_COLOR = '#b9dce0';
const SKY_TILE_WORLD_Y = 124;
const BAR_TOP_WORLD_Y = 0;
const TILE_W = 128;
const HILL_X = 109;
const HILL_Y = 168;
const ZEPPELIN_SPRITE = 'fx_57';
const ZEPPELIN_BANNER_DX = 15;
const ZEPPELIN_BANNER_DY = 7;
const CLOUD_MIN_X = -32;
const CLOUD_MAX_X = 287;
const MINIMAP_BAR_W = 64;
export interface SimSceneOptions {
  seed: number;
  opponentConfig: 0 | 1 | 2 | 3;
  opponentName: string;
  playerName: string;
  autopilot?: boolean;
  scriptedHuman?: boolean;
  fastForward?: number;
  freezeAfter?: number;
  onExit: (won: boolean, result: MatchResult) => void;
  practice?: boolean;
  playerPilot?: number;
  onResult?: (won: boolean, result: MatchResult) => void;
}
interface Cloud {
  x: number;
  y: number;
  left: boolean;
}
export class SimScene {
  private sim!: SimState;
  private human: Controller;
  private readonly keyboard = new KeyboardController();
  private readonly touch = new TouchController();
  private readonly bot: BotController;
  private readonly autopilot: BotController | null;
  private restartAsked = false;
  private down: ((e: KeyboardEvent) => void) | null = null;
  private taunt = '';
  private tauntTicks = 0;
  private readonly particles = new ParticlePool();
  private readonly particleImgs: (HTMLImageElement | undefined)[];
  private readonly smoke = new SmokeTrail();
  private readonly fxRng: FxRandom;
  private readonly clouds: Cloud[] = [];
  private readonly zeppelin = { x: 0, y: 0 };
  private banner: HTMLCanvasElement | null = null;
  private resultMusicPlayed = false;
  private resultSequence: ResultSequence | null = null;
  private resultPixels: ImageData | null = null;
  private resultUpdates = 0;
  private resultRecorded = false;
  private resultClosing: number | null = null;
  private scoreFlashTicks = 0;
  private liveTicks = 0;
  private frozen = false;
  private renderedTick = -1;
  private gameFrame: ImageData | null = null;
  private paused = false;
  private viewCam: {
    x: number;
    y: number;
  } | null = null;
  private get localSide(): 0 | 1 {
    return 0;
  }
  private pauseCursor = 0;
  private readonly pointerAction = (e: PointerEvent): void => {
    const canvas = document.getElementById('screen');
    if (!(canvas instanceof HTMLCanvasElement) || e.target !== canvas) return;
    if (this.sim.over) this.restartAsked = true;
    else if (this.paused) {
      const r = canvas.getBoundingClientRect();
      const y = ((e.clientY - r.top) / r.height) * 128;
      if (y >= 54 && y < 86) {
        this.pauseCursor = Math.floor((y - 54) / 16);
        this.pauseAction();
      }
    }
  };
  private smokeResetSerial: [number, number] = [0, 0];
  constructor(
    private readonly screen: Screen,
    private readonly assets: Assets,
    private readonly audio: AudioSystem,
    private readonly opts: SimSceneOptions,
  ) {
    this.human = opts.scriptedHuman
      ? new DuelPilot()
      : new HumanController([this.keyboard, this.touch]);
    this.bot = new BotController(opts.opponentConfig);
    this.autopilot = opts.autopilot ? new BotController(2) : null;
    this.particleImgs = particleSprites((name) => assets.sprites.get(name));
    this.fxRng = new FxRandom(opts.seed ^ 0x5f3759df);
    for (let i = 0; i < 2; i++) {
      this.clouds.push({
        x: this.fxRng.below(255),
        y: this.fxRng.below(104),
        left: this.fxRng.below(2) === 0,
      });
    }
    this.zeppelin.x = this.fxRng.below(255) << 6;
    this.zeppelin.y = this.fxRng.below(104);
    this.reset(opts.seed);
  }
  diagnostics(): Record<string, unknown> {
    return {
      scene: 'fight',
      tick: this.sim.tick,
      score: [...this.sim.score],
      over: this.sim.over,
      winner: this.sim.winner,
      practice: this.sim.practice,
      paused: this.paused,
      localSide: this.localSide,
      hash: hashState(this.sim),
      resultUpdates: this.resultUpdates,
      resultClosing: this.resultClosing,
      planes: this.sim.planes.map((p) => ({ ...p })),
      cam: { ...(this.viewCam ?? this.sim.cam) },
    };
  }
  attach(): void {
    this.keyboard.attach();
    this.touch.attach();
    this.down = (e: KeyboardEvent): void => {
      const select = ['Enter', ' ', 'z', 'Z', '5'].includes(e.key);
      if (this.sim.over && select) this.restartAsked = true;
      else if (['Escape', 'p', 'P'].includes(e.key)) {
        e.preventDefault();
        this.paused = !this.paused;
        this.keyboard.clear();
        this.touch.clear();
      } else if (this.paused) {
        if (['ArrowUp', 'ArrowDown', 'w', 's', '2', '8'].includes(e.key))
          this.pauseCursor = 1 - this.pauseCursor;
        else if (select) this.pauseAction();
      }
    };
    window.addEventListener('keydown', this.down);
    window.addEventListener('pointerdown', this.pointerAction);
    for (let i = (this.opts.fastForward ?? 0) - 1; i >= 0 && !this.sim.over; i--) this.tick();
    if (this.opts.freezeAfter === 0) this.frozen = true;
  }
  detach(): void {
    this.keyboard.detach();
    this.touch.detach();
    if (this.down) window.removeEventListener('keydown', this.down);
    window.removeEventListener('pointerdown', this.pointerAction);
  }
  private reset(seed: number): void {
    this.sim = createSim(seed, {
      practice: this.opts.practice ?? false,
      playerPilot: this.opts.playerPilot ?? 0,
      opponentPilot: this.opts.opponentConfig,
    });
    this.renderedTick = -1;
    this.resultSequence = null;
    this.resultPixels = null;
    this.resultUpdates = 0;
    this.resultRecorded = false;
    this.resultClosing = null;
    this.scoreFlashTicks = 0;
    this.smokeResetSerial = [0, 0];
    this.particles.clear();
    this.smoke.reset();
    this.resultMusicPlayed = false;
    this.liveTicks = 0;
    this.frozen = false;
  }
  update(): void {
    if (this.frozen) return;
    this.tick();
    this.liveTicks++;
    if (this.opts.freezeAfter !== undefined && this.liveTicks >= this.opts.freezeAfter)
      this.frozen = true;
  }
  private pauseAction(): void {
    if (this.pauseCursor === 0) {
      this.paused = false;
      this.keyboard.clear();
      this.touch.clear();
    } else this.exit();
  }
  private exit(): void {
    this.detach();
    this.opts.onExit(this.sim.over && this.sim.winner === this.localSide, {
      accuracy: this.sim.planes[this.localSide].accuracy,
      perfect: this.sim.planes[this.localSide].perfect,
      opponentScore: this.sim.score[1 - this.localSide]!,
      practice: this.sim.practice,
    });
  }
  private tick(): void {
    if (this.paused) return;
    if (this.sim.over) {
      const closeAutomatically = this.resultSequence?.done ?? false;
      this.resultUpdates++;
      const ctx = this.screen.ctx;
      if (this.resultUpdates <= 8 && this.gameFrame) {
        const copy = document.createElement('canvas');
        copy.width = 128;
        copy.height = 128;
        copy.getContext('2d')!.putImageData(this.gameFrame, 0, 0);
        const p = this.sim.planes[0],
          cam = this.viewCam ?? this.sim.cam,
          radius = 64 - 8 * (this.resultUpdates - 1);
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, 128, 128);
        ctx.save();
        ctx.beginPath();
        ctx.rect(p.d - cam.x - radius, p.e - cam.y - radius, radius * 2, radius * 2);
        ctx.clip();
        ctx.drawImage(copy, 0, 0);
        ctx.restore();
      } else {
        if (!this.resultMusicPlayed) {
          this.resultMusicPlayed = true;
          this.audio.music.play(SEG_FLIGHT);
          const win = this.sim.winner!;
          this.resultSequence = new ResultSequence(
            this.assets,
            this.sim.planes[win],
            this.sim.planes[(1 - win) as 0 | 1],
            this.fxRng,
          );
        }
        this.resultSequence!.paint(ctx);
        const font = this.assets.fontMain;
        font
          .wrap(this.assets.strings.tg[this.sim.winner === this.localSide ? 9 : 10] ?? '', 120)
          .forEach((line, i) => font.drawCentered(ctx, line, 64, 2 + i * font.glyphH));
        font.drawCentered(ctx, 'PRESS FIRE', 64, 118);
        if (closeAutomatically || this.restartAsked) {
          this.resultClosing ??= Math.min(264, 8 * (this.resultUpdates - 9));
          this.restartAsked = false;
        }
        const radius = this.resultClosing ?? Math.min(264, 8 * (this.resultUpdates - 9));
        if (radius <= 128) {
          const copy = document.createElement('canvas');
          copy.width = 128;
          copy.height = 128;
          copy.getContext('2d')!.drawImage(this.screen.ctx.canvas, 0, 0);
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, 128, 128);
          ctx.save();
          ctx.beginPath();
          ctx.rect(64 - radius, 64 - radius, radius * 2, radius * 2);
          ctx.clip();
          ctx.drawImage(copy, 0, 0);
          ctx.restore();
        }
      }
      this.resultPixels = ctx.getImageData(0, 0, 128, 128);
      if (this.resultClosing !== null) {
        this.resultClosing -= 8;
        if (this.resultClosing < 0) this.exit();
      }
      return;
    }
    const human = this.autopilot ?? this.human;
    stepWithControllers(this.sim, [human, this.bot]);
    if (this.sim.over && !this.resultRecorded) {
      this.resultRecorded = true;
      this.opts.onResult?.(this.sim.winner === this.localSide, {
        accuracy: this.sim.planes[this.localSide].accuracy,
        perfect: this.sim.planes[this.localSide].perfect,
        opponentScore: this.sim.score[1 - this.localSide]!,
        practice: this.sim.practice,
      });
    }
    for (const event of this.sim.events) {
      if (event.type === 'music') this.audio.music.play(event.slot);
      else if (event.type === 'crash')
        this.particles.spawnCrashBurst(this.sim.tick, event.x, event.y, event.h);
      else if (event.type === 'score') this.scoreFlashTicks = 30;
    }
    if (this.sim.taunt) {
      this.taunt = this.assets.strings.tt[this.sim.taunt.index] ?? '';
      this.tauntTicks = this.sim.taunt.ticks;
    }
    this.render(0);
  }
  private advanceZeppelin(): void {
    const drawnAt = this.zeppelin.x >> 6;
    this.zeppelin.x += 16;
    if (drawnAt > 255) {
      this.zeppelin.x = -3200;
      this.zeppelin.y = this.fxRng.below(104);
    }
  }
  private advanceCloud(c: Cloud): void {
    if (this.fxRng.below(3) !== 0) return;
    c.x += c.left ? -2 : 2;
    if (c.x < CLOUD_MIN_X) {
      c.x = CLOUD_MAX_X;
      c.y = this.fxRng.below(104);
    } else if (c.x > CLOUD_MAX_X) {
      c.x = CLOUD_MIN_X;
      c.y = this.fxRng.below(104);
    }
  }
  render(fps: number): void {
    const ctx = this.screen.ctx;
    if (this.resultPixels) {
      ctx.putImageData(this.resultPixels, 0, 0);
      return;
    }
    if (this.renderedTick === this.sim.tick) {
      if (this.gameFrame) ctx.putImageData(this.gameFrame, 0, 0);
      this.drawOverlays();
      return;
    }
    this.renderedTick = this.sim.tick;
    const { fontMain: font, fontRed: red, fontBlue: blue } = this.assets;
    ctx.fillStyle = SKY_COLOR;
    ctx.fillRect(0, 0, SCREEN_W, SCREEN_H - 4);
    const cam = this.viewCam ?? this.sim.cam;
    ctx.save();
    ctx.translate(-cam.x, -cam.y);
    this.drawWorld();
    const sprite = (name: string): HTMLImageElement | undefined => this.assets.sprites.get(name);
    for (const side of [1, 0] as const) {
      const frame = this.sim.frames[side];
      if (this.smokeResetSerial[side] !== frame.plane.resetSerial) {
        this.smoke.resetSide(side);
        this.smokeResetSerial[side] = frame.plane.resetSerial;
      }
      this.drawPlaneFrame(frame);
      this.smoke.stepAndDraw(ctx, frame.plane, cam.x, cam.y, sprite, this.fxRng);
    }
    const hill = this.assets.sprites.get('fx_01');
    if (hill) ctx.drawImage(hill, HILL_X, HILL_Y);
    this.drawClouds(cam);
    this.particles.stepAndDraw(ctx, this.particleImgs, cam.x, cam.y);
    ctx.restore();
    ctx.fillStyle = '#660000';
    ctx.fillRect(0, SCREEN_H - 2, SCREEN_W, 2);
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(Math.trunc((cam.x * MINIMAP_BAR_W) / CAM_MAX_X), SCREEN_H - 2, MINIMAP_BAR_W, 2);
    {
      const me = this.sim.planes[this.localSide]!;
      const foe = this.sim.planes[(1 - this.localSide) as 0 | 1]!;
      const target = pilotPosition(foe);
      const fdx = target.x;
      const fdy = target.y;
      const sx = fdx - cam.x;
      const sy = fdy - cam.y;
      const visible = sx - 8 >= -16 && sx - 8 <= SCREEN_W && sy - 8 >= -16 && sy - 8 <= SCREEN_H;
      if (!visible) {
        const origin = pilotPosition(me);
        const angle = angleTo(origin.x, origin.y, fdx, fdy);
        const sprites = this.assets.sprites;
        if (angle >= 45 && angle < 135) {
          const img = sprites.get('fx_63');
          if (img) {
            const y = Math.max(4, Math.min(SCREEN_H - 4, sy));
            ctx.drawImage(img, SCREEN_W - img.width, Math.round(y - img.height / 2));
          }
        } else if (angle >= 135 && angle < 225) {
          const img = sprites.get('fx_61');
          if (img) {
            const x = Math.max(4, Math.min(SCREEN_W - 4, sx));
            ctx.drawImage(img, Math.round(x - img.width / 2), SCREEN_H - img.height);
          }
        } else if (angle >= 225 && angle < 315) {
          const img = sprites.get('fx_62');
          if (img) {
            const y = Math.max(4, Math.min(SCREEN_H - 4, sy));
            ctx.drawImage(img, 0, Math.round(y - img.height / 2));
          }
        } else {
          const img = sprites.get('fx_60');
          if (img) {
            const x = Math.max(4, Math.min(SCREEN_W - 4, sx));
            ctx.drawImage(img, Math.round(x - img.width / 2), 0);
          }
        }
      }
    }
    if (this.scoreFlashTicks > 0) {
      this.scoreFlashTicks--;
      font.drawCentered(
        ctx,
        `${this.sim.score[this.localSide]} - ${this.sim.score[1 - this.localSide]}`,
        64,
        64 - (font.glyphH >> 1),
      );
    }
    if (new URLSearchParams(location.search).has('debug')) {
      const title = this.assets.strings.tm[0] ?? 'BIPLANES';
      font.drawCentered(ctx, title, SCREEN_W / 2, 2);
      font.drawCentered(ctx, `VS ${this.opts.opponentName}`, SCREEN_W / 2, 12);
      red.draw(ctx, String(this.sim.score[0]).padStart(3, '0'), 3, SCREEN_H - 10);
      blue.draw(ctx, String(this.sim.score[1]).padStart(3, '0'), 3, SCREEN_H - 19);
      const me = this.sim.planes[this.localSide]!;
      const hudParts = [`G${me.g} H${me.h} F${me.f} ${fps}F`];
      if (!this.audio.bus.enabled) hudParts.push('MUTED');
      const hud = hudParts.join(' ');
      font.draw(ctx, hud, SCREEN_W - font.measure(hud) - 3, SCREEN_H - 10);
    }
    this.gameFrame = ctx.getImageData(0, 0, 128, 128);
    this.drawOverlays();
  }
  private drawOverlays(): void {
    const ctx = this.screen.ctx,
      font = this.assets.fontMain;
    if (profileStore.profile.taunts && this.tauntTicks > 0 && this.taunt && this.sim.taunt) {
      const p = this.sim.planes[this.sim.taunt.side];
      const right = p.i === 2,
        panelX = 2;
      const panel = this.assets.sprites.get('misc_02');
      if (panel) ctx.drawImage(panel, panelX, 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(panelX + 4, 4, 117, 36);
      const portrait = this.assets.sprites.get(`fx_${p.portrait}`);
      if (portrait) ctx.drawImage(portrait, right ? 122 - portrait.width : 6, 6);
      font
        .wrap(this.taunt, 80)
        .slice(0, 4)
        .forEach((line, i) =>
          font.draw(ctx, line, right ? 86 - font.measure(line) : 42, 6 + i * font.glyphH),
        );
    }
    if (this.paused) {
      ctx.fillStyle = '#b9dce0';
      ctx.fillRect(2, 38, 124, 60);
      font.drawCentered(ctx, this.assets.strings.tm[30] ?? 'GAME PAUSED', 64, 40);
      for (let i = 0; i < 2; i++) {
        const y = 54 + i * 16;
        if (i === this.pauseCursor) {
          ctx.fillStyle = '#f1ca67';
          ctx.fillRect(3, y - 2, 122, 16);
        }
        font.draw(ctx, this.assets.strings.tm[31 + i] ?? '', 5, y);
      }
    }
  }
  private drawWorld(): void {
    const ctx = this.screen.ctx;
    const { sprites } = this.assets;
    const misc = sprites.get('misc_00');
    const bar = sprites.get('misc_01');
    for (let wx = 0; wx < 256; wx += TILE_W) {
      if (misc) ctx.drawImage(misc, wx, SKY_TILE_WORLD_Y);
      if (bar) ctx.drawImage(bar, wx, BAR_TOP_WORLD_Y);
    }
    const zep = sprites.get(ZEPPELIN_SPRITE);
    if (zep) {
      const zx = this.zeppelin.x >> 6;
      const zy = this.zeppelin.y;
      if (zx > -60 && zx < 320) {
        ctx.drawImage(zep, zx, zy);
        const banner = this.renderBanner();
        if (banner) ctx.drawImage(banner, zx + ZEPPELIN_BANNER_DX, zy + ZEPPELIN_BANNER_DY);
      }
    }
    this.advanceZeppelin();
  }
  private renderBanner(): HTMLCanvasElement | null {
    if (!this.banner) {
      this.banner = document.createElement('canvas');
      this.banner.width = 26;
      this.banner.height = 9;
    }
    const b = this.banner.getContext('2d');
    if (!b) return null;
    b.fillStyle = '#e6e3e3';
    b.fillRect(0, 0, 12, 5);
    b.fillRect(13, 0, 12, 5);
    b.fillStyle = '#ffffff';
    b.fillRect(0, 5, 12, 4);
    b.fillRect(13, 5, 12, 4);
    this.assets.fontBlue.draw(b, String(this.sim.score[0]).padStart(2, '0'), 2, 0);
    this.assets.fontRed.draw(b, String(this.sim.score[1]).padStart(2, '0'), 15, 0);
    return this.banner;
  }
  private drawSprite(draw: SpriteDraw): void {
    const img = this.assets.sprites.get(draw.name);
    if (!img) return;
    this.screen.ctx.drawImage(
      img,
      draw.x - (img.width >> 1),
      draw.y - (draw.anchor === 33 ? img.height : img.height >> 1),
    );
  }
  private drawPlaneFrame(frame: PlaneFrame): void {
    const ctx = this.screen.ctx;
    if (frame.explosion) this.drawSprite(frame.explosion);
    for (const bullet of frame.bullets) {
      ctx.fillStyle = '#ff6600';
      ctx.fillRect(bullet.x, bullet.y, 3, 3);
      ctx.fillStyle = '#ffff00';
      ctx.fillRect(bullet.x + 1, bullet.y + 1, 1, 1);
    }
    if (frame.body) this.drawSprite(frame.body);
    for (const pilot of frame.pilot) this.drawSprite(pilot);
  }
  private drawClouds(cam: { x: number; y: number }): void {
    const cloud = this.assets.sprites.get('cloud_00');
    if (!cloud) return;
    const ctx = this.screen.ctx;
    for (const c of this.clouds) {
      const sx = c.x - cam.x;
      if (sx >= -cloud.width && sx <= SCREEN_W + cloud.width)
        ctx.drawImage(cloud, c.x - (cloud.width >> 1), c.y - (cloud.height >> 1));
      this.advanceCloud(c);
    }
  }
}
